import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Badge, List, Grid, Typography, Input, Tag } from 'antd';
import { ExportOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ProjectsDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    // Local filters
    const [searchText, setSearchText] = useState('');
    const [ownerFilter, setOwnerFilter] = useState('All Owners');

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // -- Mock Data for Projects --
    const mockProjects = [
        { key: '1', project: 'JioFiber Expansion', owner: 'Akash Ambani', kpi: 5, timeline: 'On Track', risk: '3/5' },
        { key: '2', project: 'Retail OmniPilot', owner: 'Isha Ambani', kpi: 4, timeline: 'Delayed', risk: '4/5' },
        { key: '3', project: 'Green Hydrogen Pilot', owner: 'Anant Ambani', kpi: 3, timeline: 'At Risk', risk: '5/5' },
    ];

    const filteredProjects = mockProjects.filter(item => {
        const matchesSearch = item.project.toLowerCase().includes(searchText.toLowerCase()) || item.owner.toLowerCase().includes(searchText.toLowerCase());
        const matchesOwner = ownerFilter === 'All Owners' || item.owner === ownerFilter;
        return matchesSearch && matchesOwner;
    });

    const renderKPIBadge = (score: number) => {
        let color = '#52c41a'; // 5
        if (score === 4) color = '#1890ff';
        if (score === 3) color = '#faad14';
        if (score < 3) color = '#f5222d';
        return (
            <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: color,
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 'bold', margin: '0 auto'
            }}>
                {score}
            </div>
        );
    };

    const renderTimelineTag = (status: string) => {
        let color = 'default';
        if (status === 'On Track') color = 'success';
        if (status === 'Delayed') color = 'warning';
        if (status === 'At Risk') color = 'error';
        return <Tag color={color} style={{ minWidth: '80px', textAlign: 'center' }}>{status}</Tag>;
    };

    const renderRiskScore = (risk: string) => {
        const [score, total] = risk.split('/');
        const numScore = parseInt(score);
        let color = '#52c41a';
        if (numScore >= 3) color = '#faad14';
        if (numScore >= 4) color = '#f5222d'; // High risk is bad

        return <Text style={{ color: color, fontWeight: 'bold' }}>{risk}</Text>;
    };

    // Derived Metrics from filtered data
    const onTrackCount = filteredProjects.filter(p => p.timeline === 'On Track').length;
    const delayedCount = filteredProjects.filter(p => p.timeline === 'Delayed').length;
    const atRiskCount = filteredProjects.filter(p => p.timeline === 'At Risk').length;
    const avgKPI = (filteredProjects.reduce((acc, curr) => acc + curr.kpi, 0) / (filteredProjects.length || 1)).toFixed(1);

    // Sidebar Mock Data
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        {entityName} — Reliance Family Office Prototype
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Projects</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - Projects Table & Metrics */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Title level={4} className="mb-0">Projects & Opportunities</Title>
                            <div className="d-flex gap-2">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search project..."
                                    style={{ width: 200 }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                                <Select defaultValue="All Owners" onChange={setOwnerFilter} style={{ width: 140 }}>
                                    <Option value="All Owners">All Owners</Option>
                                    <Option value="Akash Ambani">Akash Ambani</Option>
                                    <Option value="Isha Ambani">Isha Ambani</Option>
                                    <Option value="Anant Ambani">Anant Ambani</Option>
                                </Select>
                                <Button type="default" className="bg-success text-white border-success">Export CSV</Button>
                            </div>
                        </div>

                        <Text type="secondary" className="mb-3 d-block">Showing {filteredProjects.length} of {mockProjects.length} projects</Text>

                        <Table
                            dataSource={filteredProjects}
                            pagination={false}
                            columns={[
                                { title: 'PROJECT', dataIndex: 'project', key: 'project', render: (text) => <b>{text}</b> },
                                { title: 'OWNER', dataIndex: 'owner', key: 'owner', render: (text) => <Text type="secondary">{text}</Text> },
                                { title: 'KPI ALIGN', dataIndex: 'kpi', key: 'kpi', align: 'center', render: (score) => renderKPIBadge(score) },
                                { title: 'TIMELINE', dataIndex: 'timeline', key: 'timeline', align: 'center', render: (status) => renderTimelineTag(status) },
                                { title: 'RISK', dataIndex: 'risk', key: 'risk', align: 'center', render: (risk) => renderRiskScore(risk) },
                                { title: 'ACTION', key: 'action', align: 'center', render: () => <a href="#">View</a> }
                            ]}
                        />

                        {/* Summary Metrics */}
                        <Row gutter={16} className="mt-4">
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f6ffed' }}>
                                    <Text type="secondary" className="d-block mb-1">On Track</Text>
                                    <Title level={3} className="text-success m-0">{onTrackCount}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fffbe6' }}>
                                    <Text type="secondary" className="d-block mb-1">Delayed</Text>
                                    <Title level={3} className="text-warning m-0">{delayedCount}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#fff1f0' }}>
                                    <Text type="secondary" className="d-block mb-1">At Risk</Text>
                                    <Title level={3} className="text-danger m-0">{atRiskCount}</Title>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div className="p-3 rounded" style={{ backgroundColor: '#f0f5ff' }}>
                                    <Text type="secondary" className="d-block mb-1">Avg KPI</Text>
                                    <Title level={3} className="text-primary m-0">{avgKPI}</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ProjectsDashboard;
